import { ColumnsCorrespondence } from '@/components/ui/columns-correspondence';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Options } from '@/components/ui/fancy-multi-select';
import { backendClient } from '@/lib/api-client';
import { buildDeleteQueryString, getDeleteAttachmentCount } from '@/lib/deleteOptions';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { FileUpload, FileUploadHandles } from '@/components/shared/FileUpload';
import { Loading, OutlookSyncSection, IsolatedDataTable, TagEditorDialog } from '@/components/shared';
import { ConnectSourcesControls } from '@/components/shared/OutlookSyncProgress';
import { useDocuments, Document } from '@/hooks/useDocuments';
import { useDeadlines } from '@/hooks/useDeadlines';
import { getTimezone } from '@/components/contexts/TimezoneContext';
import { useFolders, useFolderTree } from '@/hooks/useFolders';
import { CreateFolderDialog } from '@/components/folders';
import { FolderPlus } from 'lucide-react';
import { Button } from '@/components/ui/NewButton';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { filterFilesWithToast } from '@/lib/fileFilterUtils';
import { toolbarActionLabelClass } from '@/lib/toolbar-compact';
import { useBatchCompletionRefresh } from '@/hooks/useBatchCompletionRefresh';
import { useDeepAnalysisAvailability } from '@/hooks/useDeepAnalysisAvailability';
import { ExpandedAttachmentRows, canRowExpand } from '@/components/correspondence/ExpandedAttachmentRows';
import { Row } from '@tanstack/react-table';
import { updateTagColorMap as updateSharedTagColorMap } from '@/components/ui/column-utilities';
import { deriveFilterFields, type FilterField } from '@/components/ui/data-table-filter-toolbar';
import { DEEP_ANALYSIS_LABEL } from '@/hooks/useAnalysisSet';
import { useShellHeader } from '@/components/contexts/ShellHeaderContext';
import { isCorrespondenceFolder } from '@/utils/document-utils';
import type { DocumentDeleteOptions } from '@/types/document';
import { mutate as globalMutate } from 'swr';
import {
  FolderTableRow,
  buildFolderTableData,
  buildFolderContentCounts,
  buildFolderTreeIdSet,
  enhanceColumnsForFolderRows,
  getFolderIdsToFetch,
  getFolderTableHandlers,
  buildFolderTreeMap,
} from '@/components/folders/folder-table-utils';

// Generate a map of tag names to their colors from the database
const generateTagColorMap = (documents: Document[]): Record<string, string> => {
  const tagSet = new Set<string>();

  // Collect all distinct tags
  documents.forEach(document => {
    document.tags.forEach(tag => tagSet.add(tag));
  });

  const distinctTags = Array.from(tagSet);
  const tagColorMap: Record<string, string> = {};

  // Get tag colors from the document tag_colors if available
  distinctTags.forEach(tag => {
    // Check if we have a saved color for this tag
    if (documents.some(doc =>
      doc.tag_colors &&
      doc.tag_colors[tag] &&
      doc.tag_colors[tag] !== undefined
    )) {
      // Find the first document that has this tag and a saved color
      const docWithTagColor = documents.find(doc =>
        doc.tag_colors && doc.tag_colors[tag] && doc.tag_colors[tag] !== undefined
      );

      // Use the saved color from the database
      if (docWithTagColor && docWithTagColor.tag_colors) {
        tagColorMap[tag] = docWithTagColor.tag_colors[tag];
      } else {
        // Fallback to default gray if something went wrong
        tagColorMap[tag] = '#CCCCCC';
      }
    } else {
      // No saved color found, use default gray
      tagColorMap[tag] = '#CCCCCC';
    }
  });

  return tagColorMap;
};

let tagColorMap: Record<string, string> = {};
export const getTagColorMap = () => tagColorMap;
export const updateTagColorMap = (data: Document[]) => {
  tagColorMap = generateTagColorMap(data);
};

let uniqueTags: Options[] = [];
export const getUniqueTags = (): Options[] => uniqueTags;
export const updateUniqueTags = (documents: Document[]): void => {
  const tagSet = new Set<string>();

  documents.forEach(document => {
    document.tags.forEach(tag => tagSet.add(tag));
  });

  uniqueTags = Array.from(tagSet).map(tag => ({
    value: tag,
    label: tag,
  }));
};

let uniqueSenders: Options[] = [];
export const getUniqueSenders = (): Options[] => uniqueSenders;
export const updateUniqueSenders = (documents: Document[]): void => {
  const senderSet = new Set<string>();

  documents.forEach(document => {
    document.from.forEach(sender => senderSet.add(sender));
  });

  uniqueSenders = Array.from(senderSet).map(sender => ({
    value: sender,
    label: sender,
  }));
};

let uniqueRecipients: Options[] = [];
export const getUniqueRecipients = (): Options[] => uniqueRecipients;
export const updateUniqueRecipients = (documents: Document[]): void => {
  const recipientSet = new Set<string>();

  documents.forEach(document => {
    document.to.forEach(recipient => recipientSet.add(recipient));
  });

  uniqueRecipients = Array.from(recipientSet).map(recipient => ({
    value: recipient,
    label: recipient,
  }));
};

const Correspondence = () => {
  const { selectedCase } = useCaseContext();
  const [isUploading, setIsUploading] = useState(false);
  const fileUploadRef = useRef<FileUploadHandles>(null);
  useShellHeader({
    header: 'Correspondence',
  });

  // Target folder for drag and drop
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);

  // Use the slug from the URL instead of relying on the selectedCase.guid
  const { slug } = useParams({ from: '/matter/$slug/case-files/correspondence' });
  const search = useSearch({ from: '/matter/$slug/case-files/correspondence' });
  const navigate = useNavigate();
  const { isEnabled: deepAnalysisEnabled } = useDeepAnalysisAvailability(slug as string | undefined);
  const deepAnalysisOnly = deepAnalysisEnabled && search.view === 'deep-analysis';
  const setDeepAnalysisOnly = useCallback((nextValue: boolean) => {
    navigate({
      to: '/matter/$slug/case-files/correspondence',
      params: { slug },
      search: nextValue ? { view: 'deep-analysis' } : {},
    });
  }, [navigate, slug]);

  // Side panel for document viewing
  const { openDocumentPanel } = useSidePanel();

  // Folders state
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const { folders, mutate: mutateFolders, isLoading: foldersLoading } = useFolders(slug as string);
  const { folderTree, mutate: mutateFolderTree, isLoading: folderTreeLoading } = useFolderTree(
    slug as string,
    { analysisSetOnly: deepAnalysisOnly }
  );
  const timezone = getTimezone();

  // Find the correspondence folder and its subfolders
  const correspondenceFolder = useMemo(() => {
    if (!folders) return null;
    const folder = folders.find(isCorrespondenceFolder);
    return folder;
  }, [folders]);

  // Fetch the correspondence folder's documents as table rows. Direct child folders appear
  // as inline rows (clicking one navigates into it). Child-folder documents are not fetched
  // here — folder row counts come from the folder tree instead.
  const correspondenceFolderIds = useMemo(
    () => getFolderIdsToFetch(correspondenceFolder?.folder_id),
    [correspondenceFolder?.folder_id],
  );

  // Use the custom hook for correspondence data with folder filter. Without a resolved
  // correspondence folder there is no filter, and the request would fetch the whole matter.
  const { documents: correspondenceData, error, mutate, isLoading: documentsLoading } = useDocuments(
    correspondenceFolder ? slug as string : undefined,
    undefined, // Don't filter by document type - use folder-based filtering only
    undefined,
    correspondenceFolderIds,
    {},
    timezone,
    { analysisSetOnly: deepAnalysisOnly }
  );
  const isLoading = foldersLoading || documentsLoading;
  const { deadlines } = useDeadlines(slug as string);
  const deadlineTaskStates = useMemo(
    () => Object.fromEntries(deadlines.map(deadline => [deadline.id, deadline.state])),
    [deadlines]
  );

  // Auto-refresh when a relevant batch completes
  const handleBatchRefresh = useCallback(() => {
    mutate();
    mutateFolders();
    mutateFolderTree();
  }, [mutate, mutateFolders, mutateFolderTree]);

  useBatchCompletionRefresh(
    slug as string,
    correspondenceFolder?.folder_id,
    folders,
    handleBatchRefresh
  );

  // Update the shared state when correspondence data changes
  useEffect(() => {
    if (correspondenceData) {
      updateTagColorMap(correspondenceData);
      updateSharedTagColorMap(correspondenceData);
      updateUniqueTags(correspondenceData);
      updateUniqueSenders(correspondenceData);
      updateUniqueRecipients(correspondenceData);
    }
  }, [correspondenceData]);

  // Keep a stable ref to mutate so columns don't recompute on every SWR refresh
  const mutateRef = useRef(mutate);
  useEffect(() => { mutateRef.current = mutate; }, [mutate]);

  // Stable refreshData callback - doesn't change identity when mutate changes
  const refreshData = useCallback(() => { mutateRef.current(); }, []);

  // Keep a stable ref to correspondenceData so handleDelete doesn't need it as a dependency
  const correspondenceDataRef = useRef(correspondenceData);
  useEffect(() => { correspondenceDataRef.current = correspondenceData; }, [correspondenceData]);

  const refreshDeleteCaches = useCallback(async () => {
    if (!slug) return;

    await Promise.allSettled([
      mutateRef.current(),
      mutateFolders(),
      mutateFolderTree(),
      globalMutate(
        (key: unknown) => typeof key === 'string' && key.startsWith(`/correspondence/${slug}/fetch-all`),
        undefined,
        { revalidate: true }
      ),
    ]);
  }, [slug, mutateFolders, mutateFolderTree]);

  const handleDelete = useCallback(async (selectedIds: string[], options?: DocumentDeleteOptions) => {
    if (!slug || !correspondenceDataRef.current) return;

    const queryString = buildDeleteQueryString(options);

    // Only do optimistic update when we know deletion will proceed (force=true or initial attempt)
    if (options?.force) {
      const updatedData = correspondenceDataRef.current.filter(
        item => !selectedIds.includes(item.id)
      );
      await mutateRef.current(updatedData, false);
    }

    try {
      await backendClient.post(`/correspondence/${slug}/delete-files${queryString}`, selectedIds);
      // If this was the initial (non-force) call that succeeded, do the optimistic update now
      if (!options?.force) {
        const updatedData = correspondenceDataRef.current.filter(
          item => !selectedIds.includes(item.id)
        );
        await mutateRef.current(updatedData, false);
      }
      await refreshDeleteCaches();
    } catch (err: any) {
      // Revert optimistic update if we did one
      if (options?.force) {
        await mutateRef.current();
      }
      throw err;
    }
  }, [slug, refreshDeleteCaches]);

  const handleProcessingComplete = useCallback(() => {
    // Force a revalidation of the data when processing is complete
    mutate();
    setTargetFolderId(null); // Reset target folder after upload
  }, [mutate]);

  const handleDropFiles = useCallback(async (files: File[]) => {
    if (!slug || isUploading) return;

    // Wait for folders to load before allowing file uploads
    if (foldersLoading) {
      console.log('Folders still loading, cannot upload yet');
      toast.error('Please wait for folders to load before uploading files');
      return;
    }

    // Check if correspondence folder exists
    if (!correspondenceFolder) {
      console.log('Correspondence folder not found, folders:', folders?.map(f => ({ name: f.name, path: f.path, id: f.folder_id })));
      toast.error('Correspondence folder not found. Please refresh the page and try again.');
      return;
    }

    // Filter files using shared utility with toast notifications
    const validFiles = filterFilesWithToast(files);
    if (!validFiles) return;

    // Set the target folder ID for the dropped files
    setTargetFolderId(correspondenceFolder.folder_id.toString());

    // Open the FileUpload dialog with filtered files
    if (fileUploadRef.current) {
      fileUploadRef.current.openWithFiles(validFiles);
    }
  }, [slug, isUploading, foldersLoading, correspondenceFolder, folders]);

  const handleEmptyStateClick = () => {
    fileUploadRef.current?.openDialog();
  };

  // Handle files dropped directly onto an inline subfolder row: route the upload to that folder.
  const handleFilesDroppedOnFolder = useCallback((files: File[], folderId: number) => {
    const validFiles = filterFilesWithToast(files);
    if (!validFiles) return;

    const folderIdString = folderId.toString();
    setTargetFolderId(folderIdString);
    fileUploadRef.current?.openWithFiles(validFiles, folderIdString);
  }, []);

  // Handle folder operations
  const handleFolderRefresh = useCallback(async () => {
    try {
      // Refresh folders first
      await mutateFolders();
      await mutateFolderTree();

      // Small delay to avoid race conditions
      await new Promise(resolve => setTimeout(resolve, 100));

      // Refresh correspondence data
      await mutate();
    } catch (error) {
      console.error('Error refreshing folder data:', error);
    }
  }, [mutateFolders, mutateFolderTree, mutate]);

  // Memoize the columns to prevent filter dropdowns from closing on re-render
  const columns = useMemo(() => {
    const foldersByGuid = buildFolderTreeMap(folderTree);
    const baseColumns = ColumnsCorrespondence({
      refreshData,
      handleDelete: handleDelete,
      slug: slug as string | undefined,
      openDocumentPanel,
      deadlineTaskStates,
      deepAnalysisEnabled,
    });
    return enhanceColumnsForFolderRows(baseColumns, {
      matterSlug: slug as string,
      onRefresh: handleFolderRefresh,
      foldersByGuid,
      emptyColumnIds: ['tags', 'from', 'to', 'attachments', 'date'],
      showFolderNameInSummary: true,
      deepAnalysisEnabled,
      deepAnalysisOnly,
    });
  }, [refreshData, handleDelete, slug, openDocumentPanel, deadlineTaskStates, deepAnalysisEnabled, deepAnalysisOnly, folderTree, handleFolderRefresh]);

  // Folder-row badge counts (direct subfolder count + recursive file count) sourced from the
  // folder tree, so deeper folders don't need their documents fetched just to show a count.
  const folderContentCounts = useMemo(
    () => buildFolderContentCounts(folderTree),
    [folderTree],
  );

  // Build the combined folder + document dataset for inline folder rows. In Deep Analysis mode the
  // flat folder list is unfiltered, so restrict folder rows to the analysis-set folders in the tree.
  const tableData = useMemo<FolderTableRow<Document>[]>(() => {
    // The table only shows its loading state when it has no rows, so hold back the subfolder
    // rows until the emails arrive rather than showing a folder list that looks complete.
    if (isLoading) return [];
    // Only restrict to the analysis set once the tree has loaded. While it's still fetching (its
    // SWR key switches on the DA toggle and briefly returns []), an empty allow-list would filter
    // out every folder row.
    const allowedFolderIds = deepAnalysisOnly && !folderTreeLoading
      ? buildFolderTreeIdSet(folderTree)
      : undefined;
    return buildFolderTableData<Document>(
      folders,
      correspondenceFolder?.folder_id,
      correspondenceData || [],
      folderContentCounts,
      allowedFolderIds,
    );
  }, [isLoading, folders, correspondenceFolder?.folder_id, correspondenceData, folderContentCounts, deepAnalysisOnly, folderTreeLoading, folderTree]);

  // Shared handlers for folder-row navigation and class names.
  const folderTableHandlers = useMemo(() => getFolderTableHandlers<Document>({
    slug: slug as string,
    navigate,
    openDocumentPanel,
    canExpandDocumentRow: canRowExpand,
    deepAnalysisOnly,
  }), [slug, navigate, openDocumentPanel, deepAnalysisOnly]);

  // Config for the Filters menu shown above the table (replaces the old
  // per-column header filters). The column-driven fields are derived from each
  // column's `meta.filter`; page-specific extras are appended below. To vary
  // fields by breakpoint, filter this array with a `useIsMobile()` result.
  const filterFields = useMemo<FilterField[]>(() => {
    const fields: FilterField[] = deriveFilterFields(columns, {
      summary: { placeholder: 'Search subject, summary, filename...' },
    });
    // When Knowledge Graph / Deep Analysis is enabled, fold its view scope
    // (previously a separate toggle) into this single Filters menu.
    if (deepAnalysisEnabled) {
      fields.push({
        id: 'analysisView',
        label: 'View',
        type: 'choice',
        options: [
          { label: 'All files', value: 'all' },
          { label: `${DEEP_ANALYSIS_LABEL} only`, value: 'analysis' },
        ],
        value: deepAnalysisOnly ? 'analysis' : 'all',
        defaultValue: 'all',
        onChange: (value) => setDeepAnalysisOnly(value === 'analysis'),
      });
    }
    return fields;
  }, [columns, deepAnalysisEnabled, deepAnalysisOnly, setDeepAnalysisOnly]);

  // Handle sync completion
  const handleSyncComplete = useCallback(() => {
    // Refresh the data when sync completes
    mutate();
  }, [mutate]);

  // Memoize the sync button to prevent re-renders during sync progress updates
  const syncButton = useMemo(() => {
    return (
      <div className="flex flex-col items-end gap-2">
        <div className="flex gap-2 items-center">
          <div className="hidden lg:flex">
            <OutlookSyncSection
              matterId={slug as string}
              onSyncComplete={handleSyncComplete}
              hasCorrespondence={(correspondenceData?.length ?? 0) > 0}
              hideConnectButton
            />
          </div>
          <ConnectSourcesControls matterId={slug as string} />
          {correspondenceFolder && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCreateFolderDialogOpen(true)}
              icon={FolderPlus}
              text="Create Subfolder"
              textClassName={toolbarActionLabelClass}
              tooltip="Create Subfolder"
              aria-label="Create Subfolder"
            />
          )}
          <div className="flex flex-col relative">
            <FileUpload
              ref={fileUploadRef}
              matterGuid={slug as string}
              buttonText="Upload Files"
              onUploadComplete={handleProcessingComplete}
              onProcessingStateChange={setIsUploading}
              showAsDialog={true}
              documentType={targetFolderId || correspondenceFolder?.folder_id?.toString()}
            />
          </div>
        </div>
      </div>
    );
  }, [
    slug,
    handleProcessingComplete,
    handleSyncComplete,
    correspondenceFolder,
    targetFolderId,
    correspondenceData,
  ]);

  // Render expanded attachment rows as actual <tr> elements for column alignment.
  // Folder rows are not documents, so skip attachment rendering for them.
  const renderSubRows = useCallback((row: Row<FolderTableRow<Document>>) => {
    if (row.original.isFolderRow) return null;
    return (
      <ExpandedAttachmentRows
        row={row as Row<Document>}
        slug={slug as string}
        onDocumentClick={(guid) => openDocumentPanel({ documentGuid: guid, activeTab: 'document' })}
      />
    );
  }, [slug, openDocumentPanel]);

  return (
    <div className="overflow-x-hidden">
      {/* Render the TagEditorDialog at page level */}
      <TagEditorDialog />

      {typeof selectedCase === 'undefined' ? (
        <NoCaseSelected />
      ) : (
        <>
          <div className="px-6 py-6 relative z-20">
            {/* Documents section */}
            <div className="pl-10 lg:pl-0">
              {error ? (
                <div>Error loading correspondence data</div>
              ) : !correspondenceData || correspondenceData === undefined ? (
                <div className="flex flex-col gap-2 justify-center items-center h-full">
                  <Loading />
                  <span className="text-sm font-medium text-gray-700 relative z-10">Loading correspondence...</span>
                </div>
              ) : (
                <div className="pt-2 sm:pt-4 lg:pt-6">
                <IsolatedDataTable
                  columns={columns}
                  data={tableData}
                  refreshData={refreshData}
                  onRowsSelected={() => {}}
                  onDelete={handleDelete}
                  getDeleteAttachmentCount={(rows) =>
                    getDeleteAttachmentCount(
                      rows.filter((row) => !row.isFolderRow) as Document[]
                    )
                  }
                  isDeleting={false}
                  isLoading={isLoading}
                  syncButton={syncButton}
                  slug={slug as string}
                  documentType="Correspondence"
                  documentTypeSlug="correspondence"
                  folderGuid={correspondenceFolder?.guid}
                  onDropFiles={handleDropFiles}
                  onFilesDroppedOnFolder={handleFilesDroppedOnFolder}
                  isProcessingDrop={isUploading}
                  onEmptyStateClick={handleEmptyStateClick}
                  filterFields={filterFields}
                  initialPageSize={100}
                  enableBulkTags
                  onAnyRowClick={folderTableHandlers.onAnyRowClick}
                  getRowClassName={folderTableHandlers.getRowClassName}
                  renderSubRows={renderSubRows}
                  getRowCanExpand={folderTableHandlers.getRowCanExpand}
                  onRowActivate={folderTableHandlers.onRowActivate}
                  onRowDoubleClick={folderTableHandlers.onRowDoubleClick}
                  deepAnalysisEnabled={deepAnalysisEnabled}
                />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Create folder dialog */}
      {correspondenceFolder && (
        <CreateFolderDialog
          matterSlug={slug as string}
          parentFolder={correspondenceFolder}
          isOpen={createFolderDialogOpen}
          onClose={() => setCreateFolderDialogOpen(false)}
          onSuccess={handleFolderRefresh}
        />
      )}
    </div>
  );
};
export default Correspondence;
